import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, File, Calendar, User, Folder, Upload } from 'lucide-react';
import { getFileIcon, getFileExtension } from '../utils/fileIcons';

const API = window.location.origin || 'http://localhost:8001';

const FileSelector = ({ onFileSelect, onNewFileUpload, onClose, uploading = false, uploadProgress = 0 }) => {
  const [activeTab, setActiveTab] = useState('new'); // 'new' or 'existing'
  const [existingFiles, setExistingFiles] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    if (activeTab === 'existing') {
      fetchExistingFiles();
    }
  }, [activeTab]);

  useEffect(() => {
    // Dosyaları s3Key'e göre grupla (aynı dosyanın birden fazla göreve atanması durumu)
    const groupedFiles = {};
    existingFiles.forEach(file => {
      const key = file.s3Key || file.id; // s3Key yoksa id kullan (eski base64 dosyalar için)
      
      if (!groupedFiles[key]) {
        groupedFiles[key] = {
          ...file,
          tasks: [{ taskId: file.taskId, taskTitle: file.taskTitle, projectName: file.projectName }]
        };
      } else {
        // Aynı s3Key'e sahip başka bir kayıt, task'ı ekle
        groupedFiles[key].tasks.push({ 
          taskId: file.taskId, 
          taskTitle: file.taskTitle,
          projectName: file.projectName 
        });
      }
    });
    
    const uniqueFiles = Object.values(groupedFiles);
    
    // Arama filtresi
    if (searchTerm) {
      const filtered = uniqueFiles.filter(file => 
        file.filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
        file.tasks.some(task => 
          task.taskTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          task.projectName?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
      setFilteredFiles(filtered);
    } else {
      setFilteredFiles(uniqueFiles);
    }
  }, [searchTerm, existingFiles]);

  const fetchExistingFiles = async () => {
    setLoading(true);
    try {
      // Sayfalanmış endpoint'ten tüm dosyaları çek (selector için büyük limit)
      const response = await axios.get(`${API}/api/files?limit=500`);
      const filesData = response.data.files || response.data;
      setExistingFiles(Array.isArray(filesData) ? filesData : []);
    } catch (error) {
      console.error('Dosyalar yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file && onNewFileUpload) {
      onNewFileUpload(file);
    }
  };

  const handleExistingFileSelect = (file) => {
    setSelectedFile(file.id);
    if (onFileSelect) {
      onFileSelect(file);
    }
  };

  // Drag and Drop handlers
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (onNewFileUpload) {
        onNewFileUpload(file);
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="flex flex-col h-full w-full">
      {/* Header - Sabit */}
      <div className="px-4 sm:px-6 py-3 sm:py-4 flex-shrink-0 rounded-t-lg" style={{ background: 'var(--accent)' }}>
        <h3 className="ox-h2 text-white flex items-center gap-2">
          <div className="w-7 h-7 sm:w-9 sm:h-9 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <File className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          </div>
          Dosya Ekle
        </h3>
        <p className="text-xs mt-1 ml-9 sm:ml-11" style={{ color: 'rgba(255,255,255,0.85)' }}>Yeni dosya yükleyin veya mevcut dosyalardan seçin</p>
      </div>

      {/* Tabs - Sabit */}
      <div
        className="flex flex-shrink-0"
        style={{ borderBottom: '2px solid var(--border)', background: 'var(--surface-muted)' }}
      >
        <button
          onClick={() => setActiveTab('new')}
          className={`flex-1 px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-bold transition-all duration-200 flex items-center justify-center gap-1 sm:gap-2 relative`}
          style={{
            background: activeTab === 'new' ? 'var(--surface)' : 'transparent',
            color: activeTab === 'new' ? 'var(--accent)' : 'var(--text-muted)',
          }}
        >
          {activeTab === 'new' && (
            <div className="absolute bottom-0 left-0 right-0 h-1" style={{ background: 'var(--accent)' }}></div>
          )}
          <div
            className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center"
            style={{ background: activeTab === 'new' ? 'var(--accent)' : 'var(--surface-2)' }}
          >
            <Upload className="w-3 h-3 sm:w-4 sm:h-4" style={{ color: activeTab === 'new' ? '#fff' : 'var(--text-muted)' }} />
          </div>
          <span className="hidden sm:inline">Yeni Dosya Yükle</span>
          <span className="sm:hidden">Yeni</span>
        </button>
        <button
          onClick={() => setActiveTab('existing')}
          className={`flex-1 px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-bold transition-all duration-200 flex items-center justify-center gap-1 sm:gap-2 relative`}
          style={{
            background: activeTab === 'existing' ? 'var(--surface)' : 'transparent',
            color: activeTab === 'existing' ? 'var(--accent)' : 'var(--text-muted)',
          }}
        >
          {activeTab === 'existing' && (
            <div className="absolute bottom-0 left-0 right-0 h-1" style={{ background: 'var(--accent)' }}></div>
          )}
          <div
            className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center"
            style={{ background: activeTab === 'existing' ? 'var(--accent)' : 'var(--surface-2)' }}
          >
            <Folder className="w-3 h-3 sm:w-4 sm:h-4" style={{ color: activeTab === 'existing' ? '#fff' : 'var(--text-muted)' }} />
          </div>
          <span className="hidden sm:inline">Mevcut Dosyalardan Seç</span>
          <span className="sm:hidden">Mevcut</span>
        </button>
      </div>

      {/* Content - Scrollable */}
      <div
        className="flex-1 overflow-y-auto p-4 sm:p-6 ox-scroll"
        style={{ background: 'var(--surface-muted)' }}
      >
        {activeTab === 'new' ? (
          <div
            className={`h-full border-4 border-dashed rounded-2xl p-4 sm:p-6 w-full flex flex-col items-center justify-center text-center hover:shadow-xl transition-all duration-300 group relative overflow-hidden ${
              isDragging ? 'scale-[1.02]' : ''
            }`}
            style={{
              background: isDragging ? 'var(--accent-weak)' : 'var(--surface-muted)',
              borderColor: isDragging ? 'var(--accent)' : 'var(--border-strong)',
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              type="file"
              id="file-upload"
              className="hidden"
              accept="*/*"
              onChange={handleFileInputChange}
            />
            {/* Upload Progress Overlay */}
            {uploading && (
              <div className="absolute inset-0 z-20 backdrop-blur-sm flex flex-col items-center justify-center p-6 rounded-2xl" style={{ background: 'rgba(var(--surface-rgb,255,255,255),0.95)', backgroundColor: 'var(--surface)' }}>
                <div className="relative mb-6">
                  <div className="w-24 h-24 rounded-full flex items-center justify-center animate-pulse" style={{ background: 'var(--accent-weak)' }}>
                    <Upload className="w-12 h-12 animate-bounce" style={{ color: 'var(--accent)' }} />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: 'var(--accent)' }}>
                    {uploadProgress}%
                  </div>
                </div>
                <h3 className="ox-h2 mb-2">Dosya Yükleniyor...</h3>
                <p className="text-sm ox-text-muted mb-4">Lütfen bekleyin, dosyanız yükleniyor</p>
                <div className="w-full max-w-md">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs ox-text-dim">İlerleme</span>
                    <span className="text-sm font-bold" style={{ color: 'var(--accent)' }}>{uploadProgress}%</span>
                  </div>
                  <div className="relative w-full h-3 rounded-full overflow-hidden shadow-inner" style={{ background: 'var(--surface-2)' }}>
                    <div
                      className="absolute top-0 left-0 h-full rounded-full transition-all duration-300 ease-out shadow-lg"
                      style={{ width: `${uploadProgress}%`, background: 'var(--accent)' }}
                    >
                      <div className="absolute inset-0 bg-white/30 animate-pulse"></div>
                    </div>
                  </div>
                  <div className="mt-3 text-center">
                    {uploadProgress < 30 && <span className="text-xs ox-text-dim">📤 Dosya aktarılıyor...</span>}
                    {uploadProgress >= 30 && uploadProgress < 70 && <span className="text-xs" style={{ color: 'var(--accent)' }}>⏳ Yükleme devam ediyor...</span>}
                    {uploadProgress >= 70 && uploadProgress < 100 && <span className="text-xs" style={{ color: 'var(--accent)' }}>🚀 Neredeyse tamamlandı...</span>}
                    {uploadProgress === 100 && <span className="text-xs" style={{ color: 'var(--good)' }}>✅ Tamamlanıyor...</span>}
                  </div>
                </div>
              </div>
            )}
            
            <label
              htmlFor="file-upload"
              className="cursor-pointer flex flex-col items-center relative z-10"
            >
              <div
                className="w-14 h-14 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-2xl"
                style={{ background: 'var(--accent)' }}
              >
                <Upload className="w-7 h-7 sm:w-10 sm:h-10 text-white" />
              </div>
              <span
                className="text-sm sm:text-lg font-bold mb-1 sm:mb-2 transition-colors"
                style={{ color: isDragging ? 'var(--accent)' : 'var(--text)' }}
              >
                {isDragging ? 'Dosyayı buraya bırakın!' : 'Bilgisayarınızdan dosya seçin'}
              </span>
              <span
                className="text-xs sm:text-sm mb-3 sm:mb-4"
                style={{ color: isDragging ? 'var(--accent)' : 'var(--text-muted)', fontWeight: isDragging ? 600 : 400 }}
              >
                {isDragging ? '👆 Dosyayı şimdi bırakın' : 'veya bu alana sürükleyip bırakın'}
              </span>
              <div
                className="inline-flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:py-2 rounded-full"
                style={{ background: 'var(--accent-weak)', border: '1px solid var(--accent)' }}
              >
                <File className="w-3 h-3" style={{ color: 'var(--accent)' }} />
                <span className="text-[10px] sm:text-xs font-medium" style={{ color: 'var(--accent)' }}>Tüm dosya türleri</span>
              </div>
            </label>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Search */}
            <div className="relative mb-3">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 rounded-lg p-1.5" style={{ background: 'var(--accent)' }}>
                <Search className="text-white w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </div>
              <input
                type="text"
                placeholder="Dosya, görev veya proje ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 sm:pl-12 pr-3 py-2.5 sm:py-3 rounded-xl transition-all text-xs sm:text-sm font-medium"
                style={{ background: 'var(--surface)', border: '2px solid var(--border)', color: 'var(--text)' }}
              />
            </div>

            {/* File List */}
            <div className="space-y-2 sm:space-y-3">
              {loading ? (
                <div
                  className="text-center py-10 sm:py-12 rounded-xl"
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
                >
                  <div
                    className="inline-block animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10"
                    style={{ border: '3px solid var(--border)', borderTopColor: 'var(--accent)' }}
                  ></div>
                  <p className="font-semibold mt-3 text-xs sm:text-sm" style={{ color: 'var(--accent)' }}>Yükleniyor...</p>
                </div>
              ) : filteredFiles.length === 0 ? (
                <div
                  className="text-center py-10 sm:py-12 rounded-xl"
                  style={{ background: 'var(--surface)', border: '2px dashed var(--border-strong)' }}
                >
                  <div
                    className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center mx-auto mb-3"
                    style={{ background: 'var(--surface-2)' }}
                  >
                    <File className="w-6 h-6 sm:w-8 sm:h-8 ox-text-dim" />
                  </div>
                  <p className="font-semibold text-sm sm:text-base" style={{ color: 'var(--text)' }}>
                    {searchTerm ? 'Dosya bulunamadı' : 'Henüz dosya yüklenmemiş'}
                  </p>
                  <p className="text-xs mt-1 ox-text-muted">
                    {searchTerm ? 'Farklı bir arama terimi deneyin' : 'Yeni dosya yükleyerek başlayın'}
                  </p>
                </div>
              ) : (
                filteredFiles.map((file) => {
                  const { Icon: FileIcon, color, bgColor } = getFileIcon(file.filename);
                  const extension = getFileExtension(file.filename);
                  
                  return (
                  <div
                    key={file.id}
                    onClick={() => handleExistingFileSelect(file)}
                    className="p-2.5 sm:p-4 rounded-xl cursor-pointer transition-all duration-200 ox-row-hover"
                    style={{
                      border: selectedFile === file.id ? '2px solid var(--accent)' : '2px solid var(--border)',
                      background: selectedFile === file.id ? 'var(--accent-weak)' : 'var(--surface)',
                      color: 'var(--text)',
                    }}
                  >
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="relative flex-shrink-0">
                        <div
                          className={`w-9 h-9 sm:w-11 sm:h-11 rounded-lg flex items-center justify-center transition-all duration-200 ${
                            selectedFile === file.id ? 'shadow-md' : `bg-gradient-to-br ${bgColor}`
                          }`}
                          style={selectedFile === file.id ? { background: 'var(--accent)' } : undefined}
                        >
                          <FileIcon className={`w-4 h-4 sm:w-5 sm:h-5 ${selectedFile === file.id ? 'text-white' : color}`} />
                        </div>
                        {extension && (
                          <div
                            className="absolute -bottom-0.5 -right-0.5 text-[7px] sm:text-[8px] font-bold px-1 py-0.5 rounded"
                            style={{
                              background: 'var(--surface)',
                              color: selectedFile === file.id ? 'var(--accent)' : 'var(--text-muted)',
                              border: '1px solid var(--border)',
                            }}
                          >
                            {extension.toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-1.5 mb-1.5 sm:mb-2">
                          <div
                            className="font-bold truncate text-sm sm:text-base"
                            style={{ color: selectedFile === file.id ? 'var(--accent)' : 'var(--text)' }}
                          >
                            {file.filename}
                          </div>
                          {file.tasks && file.tasks.length > 1 && (
                            <span
                              className="flex-shrink-0 text-[9px] sm:text-[10px] font-bold px-1.5 sm:px-2 py-0.5 rounded-full shadow-sm w-fit text-white"
                              style={{ background: 'var(--warn)' }}
                            >
                              {file.tasks.length} görevde
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1 sm:gap-1.5 text-[9px] sm:text-[10px] mb-1.5 sm:mb-2">
                          {file.tasks && file.tasks.slice(0, 1).map((task, idx) => (
                            <div key={idx} className="flex flex-wrap items-center gap-1">
                              {task.projectName && (
                                <div
                                  className="flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded"
                                  style={{ background: 'var(--accent-weak)', border: '1px solid var(--accent)' }}
                                >
                                  <Folder className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" style={{ color: 'var(--accent)' }} />
                                  <span className="font-semibold truncate max-w-[60px] sm:max-w-[100px]" style={{ color: 'var(--accent)' }}>{task.projectName}</span>
                                </div>
                              )}
                              {task.taskTitle && (
                                <div
                                  className="flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded"
                                  style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}
                                >
                                  <span className="truncate max-w-[80px] sm:max-w-[120px] font-medium ox-text-muted">{task.taskTitle}</span>
                                </div>
                              )}
                            </div>
                          ))}
                          {file.tasks && file.tasks.length > 1 && (
                            <div
                              className="px-1.5 sm:px-2 py-0.5 sm:py-1 rounded font-semibold ox-text-muted"
                              style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}
                            >
                              +{file.tasks.length - 1}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1.5 sm:gap-3 text-[9px] sm:text-[10px]">
                          <div className="flex items-center gap-0.5 sm:gap-1 ox-text-muted">
                            <div className="w-4 h-4 sm:w-5 sm:h-5 rounded flex items-center justify-center" style={{ background: 'var(--surface-2)' }}>
                              <User className="w-2.5 h-2.5 sm:w-3 sm:h-3" style={{ color: 'var(--text-muted)' }} />
                            </div>
                            <span className="font-medium truncate max-w-[60px] sm:max-w-[80px]">{file.uploaderName}</span>
                          </div>
                          <div className="flex items-center gap-0.5 sm:gap-1 ox-text-muted">
                            <div className="w-4 h-4 sm:w-5 sm:h-5 rounded flex items-center justify-center" style={{ background: 'var(--surface-2)' }}>
                              <Calendar className="w-2.5 h-2.5 sm:w-3 sm:h-3" style={{ color: 'var(--text-muted)' }} />
                            </div>
                            <span className="font-medium">{formatDate(file.createdAt)}</span>
                          </div>
                          <div className="flex items-center gap-0.5 sm:gap-1">
                            <div className="w-4 h-4 sm:w-5 sm:h-5 rounded flex items-center justify-center" style={{ background: 'var(--surface-2)' }}>
                              <File className="w-2.5 h-2.5 sm:w-3 sm:h-3" style={{ color: 'var(--text-muted)' }} />
                            </div>
                            <span className="font-bold" style={{ color: 'var(--text)' }}>{formatFileSize(file.fileSize)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileSelector;
